import React, { useEffect, useState, useRef } from "react";
import "../../assets/scss/Components/Modal/modal.scss";
import { Button } from "../Button/Button";
import { VarianButtonEnum } from "../../enum/Button/variantButton.model";
import { colorButton } from "../../enum/Button/colorButton.model";
import { Typography } from "../../components/typography";
import { IconModelEnum } from "../../enum/Button/iconButton.model";
import DataContainer from "../../services/DataContainer";
import {
  ContactDealerFormSettings,
  ContactDealerSettings,
  PopupSendEmail,
  PopupShare,
  PrivacyPolicySettings
} from "../../enum/enums";
import { CustomCheckbox } from "../CustomCheckbox/CustomCheckbox";
import classNames from "classnames";
import { Input } from "../Input/Input";
import { mailFormData, mailFormDataTypes } from "./mailData";
import { Props } from "./types";
import Spinner from "../LoadingSpinner";
import { AdobeEventActions } from "../../enum/adobe/adobeEventActions";
import { StepNumbers } from "../../enum/adobe/stepNumbers";
import { AdobeEventType } from "../../enum/adobe/adobeEventType";
import { StepNames } from "../../enum/adobe/stepNames";
import { SubStepNames } from "../../enum/adobe/subStepNames";
import { trapFocus, announceToScreenReader, createFocusManager } from "../../utils/accessibility";

const EMAIL_PATTERN = /[a-zA-Z0-9]+[\\.]?([a-zA-Z0-9]+)?[\\@][a-z0-9]{2,20}[\\.][a-z]{2,9}/;

export const Modal = ({ 
  setIsOpen,
  configuration,
  frontalTruckImage, 
  locale,
}: Props) => {
  const [emailValue, setEmailValue] = useState("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const [hasErrors, setHasErrors] = useState(false);
  const [privacyCheckboxErrorIndicator, setPrivacyCheckboxErrorIndicator] = useState(false);
  const [marketingCheckboxErrorIndicator, setMarketingCheckboxErrorIndicator] = useState(false);
  const [step, setStep] = useState(1);
  const [mailModalData, setMailModalData] = useState(mailFormData);
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(false);
  
  const modalRef = useRef<HTMLDivElement>(null);
  const focusManagerRef = useRef(createFocusManager());
  const { saveFocus, restoreFocus } = focusManagerRef.current;
  
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMailModalData({ ...mailModalData, [e.target.name]: e.target.value });
  };

  const market = locale?.split("-")[1];

  const checkEmailValidity = (email: string) => {
    const result = EMAIL_PATTERN.test(email);
    setIsEmailValid(result);
    return result;
  };

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
    if (name === "privacyCheckbox") {
      setPrivacyCheckboxErrorIndicator(e.target.checked);
    } else if (name === "marketingCheckbox") {
      setMarketingCheckboxErrorIndicator(e.target.checked);
    }
    setMailModalData({ ...mailModalData, [e.target.name]: e.target.checked });
  };

  const requiredFielsAreNotEmpty = (data: mailFormDataTypes) => {
    const requiredFields = Object.entries(data).filter(([key]) => {
      return DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, `contact_dealer_${key}_required`) === "true";
    });

    return requiredFields.every(field => field[1] !== "");
  };

  const submitHandler = async () => {
    const marketingCheckboxRequired = DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true";    
    const isValidEmail = checkEmailValidity(mailModalData.email);
    if(DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SALESFORCE) === "true") {
      const requestedFrom = "MailForm";      
      if(!requiredFielsAreNotEmpty(mailModalData) || !mailModalData.email || !isValidEmail || !mailModalData.marketingCheckbox) {
        setHasErrors(true);
        setButtonDisabled(false);
      } else {
        setHasErrors(false);        
        DataContainer.sendDataToSalesForce(mailModalData, requestedFrom, locale, configuration, frontalTruckImage, async (res: any) => {                  
          if(res) {
            setStep(2);
            setStatus(res.Status);
            setMessage(res.Message);
            DataContainer.sendDataToAdobeDataLayer(AdobeEventType.APPLICATION_INTERACTION, StepNumbers.STEP_8, StepNames.SUMMARY, SubStepNames.SUMMARY, AdobeEventActions.EMAIL_CONFIGURATION, {}, "");
            setButtonDisabled(false);
          } else {
            console.error("Something went wrong with your request");
            setButtonDisabled(false);
          }
        });        
      }
    } else {      
      if (!emailValue || !isValidEmail || (marketingCheckboxRequired ? (!marketingCheckboxErrorIndicator || !privacyCheckboxErrorIndicator) : !privacyCheckboxErrorIndicator) ) {
        setHasErrors(true);
        setButtonDisabled(false);
      }
      else {
        setHasErrors(false);
        setStep(2);
        await DataContainer.sendTruckConfigurationToCostumer(configuration, emailValue, frontalTruckImage, mailModalData.marketingCheckbox, locale);
        DataContainer.sendDataToAdobeDataLayer(AdobeEventType.APPLICATION_INTERACTION, StepNumbers.STEP_8, StepNames.SUMMARY, SubStepNames.SUMMARY, AdobeEventActions.EMAIL_CONFIGURATION, {}, "");
        setButtonDisabled(false);
      }
    }
  };

  // Handle modal accessibility
  useEffect(() => {
    if (modalRef.current) {
      saveFocus();
      const cleanup = trapFocus(modalRef.current, () => {
        setIsOpen(false);
        announceToScreenReader("Modal closed");
      });
      
      // Announce modal opening
      announceToScreenReader("Modal opened");
      
      return () => {
        cleanup();
        restoreFocus();
      };
    }
  }, [setIsOpen, saveFocus, restoreFocus]);

  return (
    <div 
      ref={modalRef}
      className="email-modal h-full top-2/4 left-2/4 z-30 fixed -translate-y-1/2 -translate-x-1/2"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description">
      <div className=" flex flex-col flex-wrap fixed top-1/2 left-1/2 -translate-y-1/2 max-h-screen -translate-x-1/2">
        <div className={(`${(step === 1 && (DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SALESFORCE) === "true"))
          ? "email-modal__salesforce" : step === 1 ? "email-modal__content" : "thank-you--modal"} bg-white border border-gray-suva relative`)}>
          {step === 1 && 
                  <div className="p-6">
                    <div className="relative flex flex-row-reverse justify-between">
                      <Button
                        variant={VarianButtonEnum.DEFAULT}
                        icon={IconModelEnum.CLOSE_GREY}
                        bgColor={colorButton.WHITE}
                        onClickEvent={() => setIsOpen(false)}
                        customClassNames="border p-2 cursor-pointer opacity-50  border border-gray-stone right-3 sm:right-6 top-5"
                        ariaLabel="Close dialog"
                      />
                      <div id="modal-title" className="flex items-center h-6 text-gray-charcoal uppercase text-2xl font-bold ">
                        {DataContainer.sectionKey(
                          PopupSendEmail.SEND_EMAIL,
                          PopupSendEmail.SEND_EMAIL_TEXT
                        )}
                      </div>
                    </div>
                    <div className="pt-5">
                      <form className="ng-untouched ng-pristine ng-invalid">
                        {(DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SALESFORCE) === "true") ?
                          <div className="flex flex-col pb-3">
                            <Input
                              variant="mailForm"
                              type="text"
                              name="firstname"
                              label={DataContainer.sectionKey(
                                ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                ContactDealerFormSettings.CONTACT_DEALER_FIRSTNAME_TEXT
                              )}
                              value={mailModalData.firstname}
                              onChangeEvent={onChange}
                              isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_FIRSTNAME_REQUIRED) === "true" && hasErrors}
                            />
                            <Input
                              variant="mailForm"
                              type="text"
                              name="lastname"
                              label={DataContainer.sectionKey(
                                ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                ContactDealerFormSettings.CONTACT_DEALER_LASTNAME_TEXT
                              )}
                              value={mailModalData.lastname}
                              onChangeEvent={onChange}
                              isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_LASTNAME_REQUIRED) === "true" && hasErrors}
                            />
                            <Input
                              variant="mailForm"
                              type="text"
                              name="company"
                              label={DataContainer.sectionKey(
                                ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                ContactDealerFormSettings.CONTACT_DEALER_COMPANY_TEXT
                              )}
                              ariaLabel={`${DataContainer.sectionKey(
                                ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                ContactDealerFormSettings.CONTACT_DEALER_COMPANY_TEXT
                              )} for email configuration modal`}
                              value={mailModalData.company}
                              onChangeEvent={onChange}
                              isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_COMPANY_REQUIRED) === "true" && hasErrors}
                            />
                            <Input
                              variant="mailForm"
                              type="email"
                              name="email"
                              label={DataContainer.sectionKey(
                                ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                ContactDealerFormSettings.CONTACT_DEALER_EMAIL_TEXT
                              )}
                              value={mailModalData.email}
                              onChangeEvent={onChange}
                              isRequired={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_REQUIRED) === "true" && hasErrors}
                            />
                            {(!isEmailValid && mailModalData.email !== "") &&
                              <span className="text-red text-xl h-4">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_INVALID_TEXT)}</span>
                            }
                          </div>
                          : 
                          <div className="flex flex-col pb-3">
                            <label
                              htmlFor="email text-light-grey">{DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_TEXT)}:</label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={emailValue}
                              onChange={e => setEmailValue(e.target.value)}
                              className="border py-1.5 border-gray-spunpearl" 
                            />
                            {!emailValue && hasErrors && (
                              <Typography tag="p" className="text-2xs text-red pb-7">
                                {DataContainer.sectionKey(
                                  ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                  ContactDealerFormSettings.CONTACT_DEALER_EMAIL_REQUIRED_TEXT
                                )}
                              </Typography>
                            )}
                            {!isEmailValid && emailValue && (
                              <Typography tag="p" className="text-2xs text-red pb-7">
                                {DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_EMAIL_INVALID_TEXT)}
                              </Typography>
                            )}
                          </div>
                        }
                        <div className="mb-6 sm:mb-2 w-full text-gray-charcoal break-words whitespace-normal">
                          <div className="mb-6 sm:mb-2 w-3/4 text-gray-charcoal">
                            {(DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SALESFORCE) === "true") ?
                              <p className="private_policy_text text-gray-charcoal">
                                {DataContainer.sectionKey(
                                  PrivacyPolicySettings.PRIVACY_POLICY,
                                  PrivacyPolicySettings.PRIVACY_POLICY_TEXT
                                )}<span><a href={`https://www.volvogroup.com/en/tools/privacy/privacy-${(locale === "nl-be" || locale === "fr-be") ? locale?.split("-")[0] : market}.html`} target="_blank" rel="noreferrer" className="underline underline-offset-1 text-2xl text-blue-700 mb-4" >{`(https://www.volvogroup.com/en/tools/privacy/privacy-${market}.html)`}</a></span>
                              </p> :
                              <div>
                                <h5 className="private_policy_title">
                                  {DataContainer.sectionKey(
                                    PrivacyPolicySettings.PRIVACY_POLICY,
                                    PrivacyPolicySettings.PRIVACY_POLICY_TITLE
                                  )}
                                </h5>
                                <p className="private_policy_text text-gray-charcoal">
                                  {DataContainer.sectionKey(
                                    PrivacyPolicySettings.PRIVACY_POLICY,
                                    PrivacyPolicySettings.PRIVACY_POLICY_TEXT
                                  )}
                                </p>
                              </div>
                            }
                          </div>
                        </div>
                        <div className="flex flex-col gap-4 sm:gap-0">
                          {(DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SALESFORCE) !== "true") &&
                            <CustomCheckbox
                              name="privacyCheckbox"
                              customClassNames={classNames({
                                "border-red": !privacyCheckboxErrorIndicator && hasErrors,
                              })}
                              customLabelClasses={classNames({
                                "text-red": !privacyCheckboxErrorIndicator && hasErrors,
                              })}
                              label={DataContainer.sectionKey(
                                ContactDealerFormSettings.CONTACT_DEALER_FORM,
                                ContactDealerFormSettings.CONTACT_DEALER_OPTIN_READ_AND_AGREE_PRIVACY_TEXT
                              )}
                              onChangeEvent={(e) => onCheckboxChange(e, "privacyCheckbox")}
                            />
                          }
                          {(DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX) === "true" || DataContainer.sectionKey(ContactDealerSettings.CONTACT_DEALER_SETTINGS, ContactDealerSettings.SALESFORCE) === "true") &&
                            <CustomCheckbox
                              name="marketingCheckbox"
                              label={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_TEXT)}
                              onChangeEvent={(e) => onCheckboxChange(e, "marketingCheckbox")}
                              customClassNames={classNames({
                                "border-red": !marketingCheckboxErrorIndicator && hasErrors && DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true",
                              })}
                              customLabelClasses={classNames({
                                "text-red": !marketingCheckboxErrorIndicator && hasErrors && DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CONTACT_DEALER_OPTIN_MARKETING_CHECKBOX_REQUIRED) === "true",
                              })}
                            />
                          }
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button
                            disabled={buttonDisabled}
                            customClassNames="ml-6 mt-5 p-5 sm:m-0"
                            variant={VarianButtonEnum.SENDBUTTON}
                            text={buttonDisabled ? <Spinner className="w-full max-h-full mix-blend-color-burn" size={14} /> : DataContainer.sectionKey(PopupSendEmail.SEND_EMAIL, PopupSendEmail.SEND_EMAIL_CONFIRM_BUTTON)}
                            textTransform={true}
                            rounded={false}
                            bgColor={colorButton.BLUE_LIGHT}
                            onClickEvent={() => {
                              setButtonDisabled(true);
                              submitHandler();
                            }}
                          />
                        </div>
                      </form>
                    </div>
                  </div>}
          {step === 2 && <div className="p-6">
            <Button
              variant={VarianButtonEnum.DEFAULT}
              icon={IconModelEnum.CLOSE_GREY}
              bgColor={colorButton.WHITE}
              onClickEvent={() => setIsOpen(false)}
              customClassNames="absolute z-1 border p-2 cursor-pointer opacity-50  border border-gray-stone right-6 top-6"
            />
            <div className="flex flex-col items-center justify-center mt-10">
              <Typography tag="h3" className="text-2xl mb-6 text-light-grey">
                {(status === "Failure" || status === "Error") ? message : DataContainer.sectionKey(PopupShare.POPUP_SHARE, PopupShare.SHARE_SENT_TEXT)}
              </Typography>
              <Button
                customClassNames="ml-6 mt-8"
                variant={VarianButtonEnum.SENDBUTTON}
                text={DataContainer.sectionKey(ContactDealerFormSettings.CONTACT_DEALER_FORM, ContactDealerFormSettings.CLOSE_BUTTON_TEXT)}
                textTransform={true}
                rounded={false}
                bgColor={colorButton.BLUE_LIGHT}
                onClickEvent={() => setIsOpen(false)}
              />
            </div>
          </div>}
        </div>
      </div>
    </div>
  );
};
